"use client";

import { cn } from "@/lib/utils";
import { motion, useInView } from "motion/react";
import {
  Children,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { HTMLMotionProps } from "motion/react";
import { Copy, Download, Edit } from "lucide-react";
import { Button } from "../ui/button";
import { CodeEditDialog } from "../silae/code-edit-dialog";

interface SequenceContextValue {
  completeItem: (index: number) => void;
  activeIndex: number;
  sequenceStarted: boolean;
}

const SequenceContext = createContext<SequenceContextValue | null>(null);

const useSequence = () => useContext(SequenceContext);

const ItemIndexContext = createContext<number | null>(null);
const useItemIndex = () => useContext(ItemIndexContext);

interface AnimatedSpanProps extends Omit<HTMLMotionProps<"div">, "children"> {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  startOnView?: boolean;
}

export const AnimatedSpan = ({
  children,
  delay = 0,
  className,
  startOnView = false,
  ...props
}: AnimatedSpanProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(elementRef as React.RefObject<Element>, {
    amount: 0.3,
    once: true,
  });

  const sequence = useSequence();
  const itemIndex = useItemIndex();
  const [hasStarted, setHasStarted] = useState(false);
  useEffect(() => {
    if (!sequence || itemIndex === null) return;
    if (!sequence.sequenceStarted) return;
    if (hasStarted) return;
    if (sequence.activeIndex === itemIndex) {
      setHasStarted(true);
    }
  }, [sequence?.activeIndex, sequence?.sequenceStarted, hasStarted, itemIndex]);

  const shouldAnimate = sequence ? hasStarted : startOnView ? isInView : true;

  return (
    <motion.div
      ref={elementRef}
      initial={{ opacity: 0, y: -5 }}
      animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 0, y: -5 }}
      transition={{ duration: 0.3, delay: sequence ? 0 : delay / 1000 }}
      className={cn("grid text-sm font-normal tracking-tight", className)}
      onAnimationComplete={() => {
        if (!sequence) return;
        if (itemIndex === null) return;
        sequence.completeItem(itemIndex);
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

interface TypingAnimationProps extends Omit<
  HTMLMotionProps<"span">,
  "children"
> {
  children: string;
  className?: string;
  duration?: number;
  delay?: number;
  as?: React.ElementType;
  startOnView?: boolean;
}

export const TypingAnimation = ({
  children,
  className,
  duration = 60,
  delay = 0,
  as: Component = "span",
  startOnView = true,
  ...props
}: TypingAnimationProps) => {
  if (typeof children !== "string") {
    throw new Error("TypingAnimation: children must be a string. Received:");
  }

  const MotionComponent = useMemo(
    () =>
      motion.create(Component, {
        forwardMotionProps: true,
      }),
    [Component],
  );

  const [displayedText, setDisplayedText] = useState<string>("");
  const [started, setStarted] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);
  const isInView = useInView(elementRef as React.RefObject<Element>, {
    amount: 0.3,
    once: true,
  });

  const sequence = useSequence();
  const itemIndex = useItemIndex();

  useEffect(() => {
    if (sequence && itemIndex !== null) {
      if (!sequence.sequenceStarted) return;
      if (started) return;
      if (sequence.activeIndex === itemIndex) {
        setStarted(true);
      }
      return;
    }

    if (!startOnView) {
      const startTimeout = setTimeout(() => setStarted(true), delay);
      return () => clearTimeout(startTimeout);
    }

    if (!isInView) return;

    const startTimeout = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(startTimeout);
  }, [
    delay,
    startOnView,
    isInView,
    started,
    sequence?.activeIndex,
    sequence?.sequenceStarted,
    itemIndex,
  ]);

  useEffect(() => {
    if (!started) return;

    let i = 0;
    setDisplayedText(""); // Réinitialiser seulement au début de l'effet

    const typingEffect = setInterval(() => {
      if (i < children.length) {
        setDisplayedText(children.substring(0, i + 1));
        i++;
      } else {
        clearInterval(typingEffect);
        if (sequence && itemIndex !== null) {
          sequence.completeItem(itemIndex);
        }
      }
    }, duration);

    return () => {
      clearInterval(typingEffect);
    };
  }, [children, started]); // Retirer 'duration' des dépendances

  return (
    <MotionComponent
      ref={elementRef}
      className={cn("text-sm font-normal tracking-tight", className)}
      {...props}
    >
      {displayedText}
    </MotionComponent>
  );
};

interface TerminalProps {
  children: React.ReactNode;
  className?: string;
  sequence?: boolean;
  startOnView?: boolean;
  onCopy?: () => void;
  onDownload?: () => void;
  onEdit?: (editedCode: string) => void;
  editableCode?: string;
}

export const Terminal = ({
  children,
  className,
  sequence = true,
  startOnView = true,
  onCopy,
  onDownload,
  onEdit,
  editableCode,
}: TerminalProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(containerRef as React.RefObject<Element>, {
    amount: 0.3,
    once: true,
  });

  // État pour le popup d'édition
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Fonctions pour gérer l'édition
  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = (editedCode: string) => {
    if (onEdit) {
      onEdit(editedCode);
    }
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const sequenceHasStarted = sequence ? !startOnView || isInView : false;

  const contextValue = useMemo<SequenceContextValue | null>(() => {
    if (!sequence) return null;
    return {
      completeItem: (index: number) => {
        setActiveIndex((current) =>
          index === current ? current + 1 : current,
        );
      },
      activeIndex,
      sequenceStarted: sequenceHasStarted,
    };
  }, [sequence, activeIndex, sequenceHasStarted]);

  const wrappedChildren = useMemo(() => {
    if (!sequence) return children;
    const array = Children.toArray(children);
    return array.map((child, index) => (
      <ItemIndexContext.Provider key={index} value={index}>
        {child as React.ReactNode}
      </ItemIndexContext.Provider>
    ));
  }, [children, sequence]);

  const content = (
    <div
      ref={containerRef}
      className={cn(
        "z-0 h-full  w-full max-w-lg rounded-xl border border-border bg-background",
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-border p-3">
        <div className="flex flex-row gap-x-2">
          <div className="h-2 w-2 rounded-full bg-red-500"></div>
          <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
          <div className="h-2 w-2 rounded-full bg-green-500"></div>
        </div>
        <div className="flex items-center gap-x-1">
          {onCopy && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCopy}
              title="Copier le code"
              className="h-7 w-7 p-0 hover:bg-white/10 dark:hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <Copy className="h-3 w-3" />
            </Button>
          )}
          {onDownload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDownload}
              title="Télécharger le fichier"
              className="h-7 w-7 p-0 hover:bg-white/10 dark:hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <Download className="h-3 w-3" />
            </Button>
          )}
          {onEdit && editableCode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEditClick}
              title="Modifier le code"
              className="h-7 w-7 p-0 hover:bg-white/10 dark:hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <Edit className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
      <pre className="p-2 sm:p-4 text-xs sm:text-sm overflow-x-auto">
        <code className="grid gap-y-1 break-all whitespace-pre-wrap">
          {wrappedChildren}
        </code>
      </pre>
    </div>
  );

  const terminalContent = !sequence ? (
    content
  ) : (
    <SequenceContext.Provider value={contextValue}>
      {content}
    </SequenceContext.Provider>
  );

  return (
    <>
      {terminalContent}

      {/* Popup d'édition */}
      <CodeEditDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        initialCode={editableCode || ""}
        onSave={handleSaveEdit}
        title="Modifier le code SILAE"
        description="Vous pouvez modifier le code généré manuellement. Les modifications seront appliquées immédiatement."
        placeholder="Code SILAE..."
        language="silae"
      />
    </>
  );
};
